import { t } from '@lingui/macro';

import { Button, Flex, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import {
  MaximalCardEventTags,
  SearchResultCardContactClickEvent,
  SearchResultCardViewProfileClickEvent,
} from '@rover/react-lib/src/pages/search/SearchPage/analyticsEvents';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { CommonSearchResult } from '@rover/types/src/CommonSearchResult';
import { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';

import SimplifiedPriceColumn from './PriceColumn/SimplifiedPriceColumn';
import { SEARCH_RESULT_CARD_Z_INDEX } from './SearchResultCard.constants';

export type Props = {
  searchResult: CommonSearchResult;
  language: string;
  showFromText: boolean;
  isBHUser: boolean;
  shouldShowPriceInCredits: boolean;
  priceTransparencyLevel?: PriceTransparencyLevel;
  profileUrl: string;
  analyticsProps: MaximalCardEventTags;
  isDesktop?: boolean;
  isSeoPage?: boolean;
  isV2?: boolean;
};

const MaximalCTABox = ({
  searchResult: { currencyCode, price, priceInCredits, unitAsSentence, cancellationPolicy },
  language,
  showFromText,
  isBHUser,
  shouldShowPriceInCredits,
  priceTransparencyLevel,
  profileUrl,
  analyticsProps,
  isDesktop,
  isSeoPage,
  isV2,
}: Props): JSX.Element => {
  const contactUrl = `${profileUrl.replace(
    /\/\?/,
    '/contact/?'
  )}&initiation_source=search_card_contact`;
  const theme = useTheme();
  const hrefTarget = isDesktop && !isSeoPage ? '_blank' : '_self';
  return (
    <Flex
      flexDirection="column"
      minHeight={isV2 ? undefined : '32x'}
      width="100%"
      backgroundColor={isV2 ? undefined : theme.background.accent_secondary}
      justifyContent="space-between"
      p="4x"
      gap="4x"
      sx={{ borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px', marginTop: 'auto' }}
    >
      {/* V2 hides the price + cancellation policy here; the price is relocated in a follow-up PR. */}
      {!isV2 && (
        <Flex flexDirection="column" gap="2x">
          <SimplifiedPriceColumn
            variant="maximal"
            currencyCode={currencyCode}
            language={language}
            price={price}
            priceInCredits={priceInCredits}
            shouldShowPriceInCredits={shouldShowPriceInCredits}
            showFromText={showFromText}
            unitAsSentence={unitAsSentence}
            priceTransparencyLevel={priceTransparencyLevel}
            isBHUser={isBHUser}
          />
          {cancellationPolicy && (
            <Text size="100" textColor="tertiary">
              {cancellationPolicy}
            </Text>
          )}
        </Flex>
      )}
      <Flex
        flexDirection="row"
        gap="4x"
        position="relative"
        zIndex={SEARCH_RESULT_CARD_Z_INDEX.INTERACTIVE.toString()}
      >
        <Button
          variant="default"
          width="100%"
          size="small"
          href={profileUrl}
          webProps={{ hrefTarget }}
          onPress={() => {
            emitAnalyticsEvent(new SearchResultCardViewProfileClickEvent(analyticsProps));
          }}
        >
          {t`Full profile`}
        </Button>
        <Button
          variant="primary"
          width="100%"
          size="small"
          href={contactUrl}
          webProps={{ hrefTarget }}
          onPress={() => {
            emitAnalyticsEvent(new SearchResultCardContactClickEvent(analyticsProps));
          }}
        >
          {t`Contact`}
        </Button>
      </Flex>
    </Flex>
  );
};

export default MaximalCTABox;
